<script>
import logo from "@/mixins/logo";
import userService from "@/services/user";
import segmentService from "@/services/segment";
import { SELF_SIGNUP_ENABLED } from "@/services/constants";

/**
 * @component SelfSignUp
 * @description Formulario UNIFICADO de autoregistro de socios — pega
 *              contra `POST /users/self-signup`, que del lado del backend
 *              SÍ es un facade real (`resolveExternalEngine`): resuelve
 *              el motor externo de elegibilidad del tenant (ej. Dyntech
 *              para Corporación Médica) y decide qué campos del body usa
 *              cada uno. Este componente, en cambio, NO dispatchea entre
 *              implementaciones por motor — es un único form que crece
 *              de forma aditiva: si mañana un motor nuevo necesita un
 *              input que Dyntech no usa, se agrega acá (el backend de
 *              Dyntech simplemente lo ignora). Mismo shell visual que
 *              `PasswordForgot.vue`.
 *              NO auto-loguea con el token que devuelve el backend —
 *              `provisionPatientUser` le setea al User una password
 *              aleatoria que el socio nunca conoce, así que sin pasar por
 *              el link de invitación no podría volver a entrar. Mismo
 *              criterio que `PasswordForgot.vue`: mensaje + redirect a
 *              `/login`, sin tocar `store`/sesión.
 *              Ruta `/signup` — gateado por `SELF_SIGNUP_ENABLED`
 *              (`services/constants.js`, único source of truth
 *              compartido con el link en `Login.vue`): si el tenant no
 *              está habilitado, redirige a `/login` sin renderizar nada.
 *              Distinto de `/login/:orgSlug`, que ante un slug inválido
 *              NO redirige — cae silencioso al branding default del
 *              tenant y sigue mostrando el form. Acá sí tiene sentido
 *              redirigir: no hay nada útil que mostrar sin motor externo.
 */
export default {
  name: "SelfSignUp",

  mixins: [logo],

  data() {
    return {
      dni: "",
      firstName: "",
      lastName: "",
      email: "",
      birthDate: null,
      isLoading: false,
      isEnabled: SELF_SIGNUP_ENABLED,
    };
  },

  created() {
    if (!this.isEnabled) {
      this.$router.replace({ name: "login" });
    }
  },

  methods: {
    signUp() {
      segmentService.track({ name: "Self Signup Attempt" });
      this.isLoading = true;

      userService
        .selfSignup({
          dni: this.dni,
          bornAt: this.birthDate,
          email: this.email,
          firstName: this.firstName,
          lastName: this.lastName,
        })
        .then(() => {
          this.$message.success(
            "¡Listo! Vas a recibir un link para definir tu contraseña"
          );
          this.$router.push({ name: "login" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<template lang="pug">
  .login(v-if="isEnabled")
    .login__container
      ValidationObserver(v-slot="{handleSubmit}")
        form.login__card(@submit.prevent="handleSubmit(signUp)")
          .login__img
            img.login__svg(v-if="logoAltUrl" :src="logoAltUrl" alt="Logo")
            micon.login__svg(v-else :name="'quantux-logo-horizontal-nuevo'")
          ValidationProvider(name="DNI" rules="required" v-slot="{ errors }")
            el-input#dni(placeholder="DNI" v-model="dni" autocapitalize="none")
            span.has-error {{ errors[0] }}
          ValidationProvider(name="Nombre" rules="required" v-slot="{ errors }")
            el-input#firstName(placeholder="Nombre" v-model="firstName")
            span.has-error {{ errors[0] }}
          ValidationProvider(name="Apellido" rules="required" v-slot="{ errors }")
            el-input#lastName(placeholder="Apellido" v-model="lastName")
            span.has-error {{ errors[0] }}
          ValidationProvider(name="Fecha de nacimiento" rules="required" v-slot="{ errors }")
            el-date-picker.login__date(
              v-model="birthDate"
              type="date"
              placeholder="Fecha de nacimiento"
              format="dd/MM/yyyy"
              value-format="yyyy-MM-dd"
            )
            span.has-error {{ errors[0] }}
          ValidationProvider(name="Email" rules="required|email" v-slot="{ errors }")
            el-input#email(placeholder="Email" v-model="email" autocapitalize="none")
            span.has-error {{ errors[0] }}
          el-button(type="primary" native-type="submit" :loading="isLoading") Registrarme
          router-link.login__link(:to="{ name: 'login' }") ¿Ya tenés cuenta? Ingresá haciendo click aquí
</template>

<style lang="scss" scoped>
.login__date {
  width: 100%;
  margin-bottom: 10px;
}
</style>
